import { createFocusTrap, type FocusTrap } from 'focus-trap';
import { BREAKPOINT_MEDIA_QUERIES } from '../utils/breakpoints';
import { defineModule } from '../utils/helpers';
import { Overlay, hasOpenOverlay, toggleOverlay } from '../utils/overlays';

let focusTrap: FocusTrap | null = null;

const getElements = () => ({
  menuElement: document.querySelector<HTMLElement>('[data-menu]'),
  toggleElement: document.querySelector<HTMLButtonElement>('.menu__toggle'),
  backdropElement: document.querySelector<HTMLElement>('[data-menu-backdrop]'),
  closeElement: document.querySelector<HTMLButtonElement>('[data-menu-close]'),
  drawerElement: document.querySelector<HTMLElement>('.menu__drawer'),
});

const openMenu = () => {
  const { toggleElement, drawerElement } = getElements();
  if (!toggleElement) return;

  toggleOverlay(Overlay.MENU, true);
  document.body.classList.add('menu--open');
  toggleElement.ariaExpanded = 'true';

  if (!drawerElement) return;

  drawerElement.removeAttribute('inert');
  drawerElement.removeAttribute('aria-hidden');

  // The drawer is `lg:hidden`, so from that breakpoint up it holds no tabbable
  // nodes and focus-trap would have nothing to activate on.
  if (BREAKPOINT_MEDIA_QUERIES.lg.matches) return;

  focusTrap = createFocusTrap(drawerElement, {
    // Escape is handled below so the body class and the trap stay in step.
    escapeDeactivates: false,
    // The backdrop sits outside the drawer and closes the menu.
    allowOutsideClick: true,
  });
  focusTrap.activate();
};

const closeMenu = () => {
  const { toggleElement, drawerElement } = getElements();
  if (!toggleElement) return;

  toggleOverlay(Overlay.MENU, false);
  document.body.classList.remove('menu--open');
  toggleElement.ariaExpanded = 'false';

  // Deactivating hands focus back to the toggle, so it has to happen before the
  // drawer goes inert — focus cannot stay inside an inert subtree.
  focusTrap?.deactivate();
  focusTrap = null;

  if (!drawerElement) return;

  drawerElement.setAttribute('inert', '');
  drawerElement.setAttribute('aria-hidden', 'true');
};

const handleToggle = () => {
  if (hasOpenOverlay(Overlay.MENU)) {
    closeMenu();
  } else {
    openMenu();
  }
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && hasOpenOverlay(Overlay.MENU)) {
    closeMenu();
  }
};

const handleScroll = () => {
  const { menuElement } = getElements();
  if (!menuElement) return;

  menuElement.classList.toggle('menu--scrolled', window.scrollY > 8);
};

const handleBreakpointChange = () => {
  if (BREAKPOINT_MEDIA_QUERIES.lg.matches) {
    closeMenu();
  }
};

export default defineModule(
  () => {
    const { toggleElement, backdropElement, closeElement } = getElements();
    if (!toggleElement) return;

    toggleElement.addEventListener('click', handleToggle);
    backdropElement?.addEventListener('click', closeMenu);
    closeElement?.addEventListener('click', closeMenu);
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('scroll', handleScroll, { passive: true });
    BREAKPOINT_MEDIA_QUERIES.lg.addEventListener(
      'change',
      handleBreakpointChange,
    );

    handleScroll();
  },
  () => {
    const { toggleElement, backdropElement, closeElement } = getElements();
    if (!toggleElement) return;

    toggleElement.removeEventListener('click', handleToggle);
    backdropElement?.removeEventListener('click', closeMenu);
    closeElement?.removeEventListener('click', closeMenu);
    window.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('scroll', handleScroll);
    BREAKPOINT_MEDIA_QUERIES.lg.removeEventListener(
      'change',
      handleBreakpointChange,
    );

    closeMenu();
  },
);
